import { FiArrowUpRight, FiLogOut } from "react-icons/fi";
import "./AccountSettingsMenu.css";
import { IS_APP_V1, SHOW_BUY_MORE_CREDITS } from "../../config/app/appVersion";
import { getUserDisplayName } from "../../utils/displayName";

const SETTINGS_LINKS = [
  { id: "profile", label: "Profile" },
  { id: "subscriptions", label: "Subscriptions" },
  { id: "billingHistory", label: "Billing History" },
  { id: "usageHistory", label: "Usage History" },
  { id: "settings", label: "Settings" },
];

const AccountSettingsMenu = ({
  isOpen,
  onClose = () => {},
  user,
  creditsBalance = 0,
  lifetimeCredits,
  monthlyCredits = 0,
  storageQuotaGb = 2,
  storageUsedGb = 0,
  onOpenSettingsSection = () => {},
  onBuyMoreCredits = () => {},
  onLogout = () => {},
  logoutOnly = IS_APP_V1,
}) => {
  if (!isOpen) return null;

  const displayName = getUserDisplayName(user);

  const email = user?.primaryEmailAddress?.emailAddress || "";

  const lifetimeDisplay =
    lifetimeCredits ?? creditsBalance;

  const monthlyDisplay = logoutOnly ? 0 : monthlyCredits;

  // Map credits to a 0–100% width for the bar.
  // Adjust 1000 to whatever you consider "full" credits.
  const maxCreditsForBar = Math.max(1000, Number(lifetimeDisplay) || 0);
  const progressPercent = Math.max(
    0,
    Math.min((lifetimeDisplay / maxCreditsForBar) * 100, 100)
  );
  const storageProgress = Math.max(
    0,
    Math.min(
      storageQuotaGb > 0 ? (storageUsedGb / storageQuotaGb) * 100 : 0,
      100
    )
  );
  const showBuyMoreCredits = !logoutOnly && (SHOW_BUY_MORE_CREDITS || !IS_APP_V1);

  return (
    <div className="account-settings-menu-backdrop" onClick={onClose}>
      <div
        className="account-settings-menu"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="account-settings-header">
          <img
            className="account-settings-avatar"
            src={user?.imageUrl}
            alt={displayName}
          />
          <div className="account-settings-user-info">
            <div className="account-settings-name">{displayName}</div>
            {email && <div className="account-settings-email">{email}</div>}
          </div>
        </div>

        <div className="account-settings-credits-row">
          <div className="account-settings-credit-pill">
            <span className="credit-count">{lifetimeDisplay}</span>
            <span className="credit-period">Lifetime</span>
            <span className="credit-label">Credits</span>
          </div>
          <div className="account-settings-credit-pill">
            <span className="credit-count">{monthlyDisplay}</span>
            <span className="credit-period">Monthly</span>
            <span className="credit-label">Credits</span>
          </div>
        </div>

        {!logoutOnly && (
          <div className="account-settings-meters">
            <div className="account-settings-progress-container">
              <div
                className="account-settings-progress-bar-bg"
                role="progressbar"
                aria-label="Credits balance"
                aria-valuemin={0}
                aria-valuemax={maxCreditsForBar}
                aria-valuenow={Number(lifetimeDisplay) || 0}
              >
                <div
                  className="account-settings-progress-bar-fill account-settings-progress-bar-fill--credits"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="account-settings-progress-label">
                <span>Credits</span>
                <span>
                  {Number(lifetimeDisplay || 0).toLocaleString()} /{" "}
                  {maxCreditsForBar.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="account-settings-progress-container">
              <div
                className="account-settings-progress-bar-bg"
                role="progressbar"
                aria-label="Storage used"
                aria-valuemin={0}
                aria-valuemax={storageQuotaGb}
                aria-valuenow={storageUsedGb}
              >
                <div
                  className="account-settings-progress-bar-fill account-settings-progress-bar-fill--storage"
                  style={{ width: `${storageProgress}%` }}
                />
              </div>
              <div className="account-settings-progress-label">
                <span>Storage used</span>
                <span>
                  {storageUsedGb} GB / {storageQuotaGb} GB
                </span>
              </div>
            </div>

            {showBuyMoreCredits ? (
              <button
                type="button"
                className="account-settings-buy-credits-btn"
                onClick={onBuyMoreCredits}
              >
                Buy More Credits
              </button>
            ) : null}
          </div>
        )}

        <div className="account-settings-menu-items">
          {!logoutOnly && (
            <>
              {SETTINGS_LINKS.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  className="account-settings-item-btn"
                  onClick={() => onOpenSettingsSection(link.id)}
                >
                  <span className="account-settings-item-label">{link.label}</span>
                  <FiArrowUpRight
                    className="account-settings-item-popup-icon"
                    aria-hidden="true"
                  />
                </button>
              ))}
            </>
          )}
          <button
            type="button"
            className={`account-settings-item-btn account-settings-logout-btn${
              logoutOnly ? " account-settings-logout-btn--solo" : ""
            }`}
            onClick={onLogout}
          >
            <span className="account-settings-item-label">Logout</span>
            <FiLogOut className="account-settings-logout-icon" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsMenu;

